import { Button, Stack } from '@mui/material'
import * as monaco from 'monaco-editor'
import { lensPath, omit, over, pick, pipe, prop, set, without } from 'ramda'
import { useCallback, useState } from 'react'

import { RecipeTextEditor } from '@/components'
import {
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'

import { DataIntakeSettings } from '../types'

import IModelDecoration = monaco.editor.IModelDecoration
import { useQuery } from '@tanstack/react-query'

import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { fetchVailHistory, useSchema } from '@/hooks'

type DataIntakeEditorValue = {
  errors: IModelDecoration[]
  text: string
}

const fetchData = async (componentName: string, version?: string | null) => {
  return {
    ...(await fetchVailHistory(version).then(
      (res) => res?.data?.getVailHistory
    )),
    componentName,
  }
}

const useListRecipes = (recipes: DataIntakeSettings['customRecipes']) => {
  return useQuery({
    queryFn: async () => {
      const historyRecipes = await Promise.all(
        recipes.map(({ version, componentName }) =>
          fetchData(componentName, version)
        )
      )

      const returnValue = historyRecipes
        .map((historyRecipe) =>
          historyRecipe
            ? `${historyRecipe.componentName}|${historyRecipe.id}`
            : null
        )
        .filter(Boolean) as string[]

      return returnValue
    },
    queryKey: ['monaco-editor', ...recipes.map(prop('componentName'))],
  })
}
export const Conversation = () => {
  const { componentID } = useComponentProvider()

  const current = useGetCustomComponent<DataIntakeSettings>(componentID)
  const update = useUpdateCustomComponent<DataIntakeSettings>(componentID)
  const recipes = useListRecipes(current.data?.settings.customRecipes ?? [])
  const [value, setValue] = useState<DataIntakeEditorValue>({
    errors: [],
    text: current.data?.settings?.dataIntake ?? '',
  })
  const schema = useSchema({ name: 'DataIntakeConfig', version: '2.1.0' })

  const generateSchema = useCallback(
    (schema: object) => {
      const parseSchema = pipe(
        set(lensPath(['required']), ['data']),
        over(lensPath(['properties']), pick(['data'])),
        over(
          lensPath(['definitions', 'Data', 'properties']),
          omit(['id', 'name'])
        ),
        over(
          lensPath(['definitions', 'Data', 'required']),
          without(['id', 'name'])
        ),
        over(lensPath(['definitions', 'Item', 'required']), without(['id'])),
        set(
          lensPath(['definitions', 'Item', 'properties', 'customRecipePath']),
          {
            description:
              'The recipes that were selected previously, the value will be {name|version} for simpler creation',
            enum: recipes.data || [],
          }
        )
      )

      return parseSchema(schema)
    },
    [recipes.data]
  )

  const onChange = useCallback(({ errors, text }: DataIntakeEditorValue) => {
    setValue({ errors, text })
  }, [])

  const saveDataIntake = async () => {
    await update.mutateAsync({
      id: componentID,
      settings: {
        ...(current.data?.settings || {}),
        dataIntake: value.text,
      },
    })
  }

  if (recipes.isLoading || schema.isLoading) return <div>Loading...</div>

  return (
    <Stack height="100%">
      <Button
        sx={{ alignSelf: 'flex-end' }}
        variant="contained"
        onClick={saveDataIntake}
      >
        Save
      </Button>
      <RecipeTextEditor
        schema={generateSchema(schema.data)}
        value={value.text}
        onChange={onChange}
      />
    </Stack>
  )
}
